// Boot intro: GB4K spelled out in flying voxel cubes over a neon synthwave
// grid, with "GB4K PRESENTS ..." across the top. Ported from the Second Opus
// voxel scene. Runs as the engine's boot scene, then hands off to the terminal.
import type { Scene } from './Engine';
import { HDR, program, deleteProgram, perspective, lookAt, mmul, rng, clamp, lerp, smooth, type Program, type V3 } from './gl';

const VS_CUBE = `#version 300 es
layout(location=0) in vec3 aP; layout(location=1) in vec3 aN;
layout(location=2) in vec4 iPS; layout(location=3) in vec4 iC; layout(location=4) in vec4 iR;
uniform mat4 VP; uniform float M;
out vec3 vN,vW,vL; out vec4 vC;
mat3 axang(vec3 a,float t){a=normalize(a+1e-5);float c=cos(t),s=sin(t),ic=1.-c;
  return mat3(c+a.x*a.x*ic,a.y*a.x*ic+a.z*s,a.z*a.x*ic-a.y*s, a.x*a.y*ic-a.z*s,c+a.y*a.y*ic,a.z*a.y*ic+a.x*s, a.x*a.z*ic+a.y*s,a.y*a.z*ic-a.x*s,c+a.z*a.z*ic);}
void main(){mat3 r=axang(iR.xyz,iR.w);vec3 w=r*(aP*iPS.w)+iPS.xyz;vN=r*aN;
  if(M<0.){w.y=-w.y;vN.y=-vN.y;} vW=w;vL=aP;vC=iC;gl_Position=VP*vec4(w,1);}`;
const FS_CUBE = HDR + `
in vec3 vN,vW,vL; in vec4 vC;
uniform vec3 CAM; uniform float M,MF;
void main(){
  vec3 n=normalize(vN), v=normalize(CAM-vW);
  vec3 l1=normalize(vec3(.4,.8,.5)), l2=normalize(vec3(-.6,.3,-.4));
  float dif=max(dot(n,l1),0.)*.75+max(dot(n,l2),0.)*.3+.28;
  vec3 s=abs(vL)*2.; float mx=max(s.x,max(s.y,s.z)), mn=min(s.x,min(s.y,s.z)), mid=s.x+s.y+s.z-mx-mn;
  float edge=smoothstep(.76,.95,mid);
  vec3 c=vC.rgb*dif;
  c+=edge*.45*mix(vC.rgb,vec3(1),.5);
  c+=pow(1.-max(dot(n,v),0.),3.)*.35*vC.rgb;
  c+=pow(max(dot(reflect(-l1,n),v),0.),24.)*.5;
  if(M<0.) c*=.7*MF*exp(vW.y*.28);
  O=vec4(c,1.);
}`;
const VS_FLOOR = `#version 300 es
uniform mat4 VP; out vec3 vW;
const vec2 Qd[6]=vec2[6](vec2(-1,-1),vec2(1,-1),vec2(1,1),vec2(-1,-1),vec2(1,1),vec2(-1,1));
void main(){vec2 q=Qd[gl_VertexID]*300.;vW=vec3(q.x,0.,q.y);gl_Position=VP*vec4(vW,1);}`;
const FS_FLOOR = HDR + `
in vec3 vW; uniform vec3 CAM,GC,HC; uniform float A;
void main(){
  vec2 p=vW.xz*.5;
  vec2 g=abs(fract(p-.5)-.5)/max(fwidth(p),vec2(1e-4));
  float line=1.-min(min(g.x,g.y),1.);
  float dist=length(vW.xz-CAM.xz);
  float fade=exp(-dist*.03), haze=1.-exp(-dist*.014);
  vec3 c=mix(vec3(0.,0.,.012),HC,haze)+GC*line*fade*(1.+K*.7);
  float a=max(mix(A,1.,haze),line*fade);
  O=vec4(c*a,a);
}`;
const FS_SKY = HDR + `
uniform mat4 IVP;
vec3 rdir(){vec2 ndc=gl_FragCoord.xy/R*2.-1.;vec4 a=IVP*vec4(ndc,1.,1.);vec4 b=IVP*vec4(ndc,-1.,1.);return normalize(a.xyz/a.w-b.xyz/b.w);}
void main(){
  vec3 d=rdir(); float e=d.y; float az=atan(d.x,-d.z);
  float mir=e<0.?.45:1.; e=abs(e);
  vec3 col=mix(vec3(.2,.05,.28),vec3(.015,.008,.04),smoothstep(0.,.45,e));
  col+=vec3(.55,.12,.7)*exp(-e*18.)*.5;
  for(int i=0;i<7;i++){float fi=float(i);
    float a0=(fi-3.)*.42+sin(T*.31+fi*1.7)*.18;
    float tilt=sin(T*.47+fi*2.1)*1.1;
    float x=abs(az-a0-e*tilt);
    float b=(exp(-x*70.)*.5+exp(-x*400.))*smoothstep(0.,.05,e)*exp(-e*1.6);
    col+=vec3(.6,.28,1.)*b*(.35+K*.7);
  }
  O=vec4(col*mir,1.);
}`;

const FONT: Record<string, string[]> = {
  G: ['.####', '#....', '#....', '#.###', '#...#', '#...#', '.####'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
};
const WORD = 'GB4K';
const LPAL: V3[] = [[1, .42, .24], [1, .48, .78], [.9, .17, .82], [.23, .36, 1], [.1, .84, .63], [.85, .88, .13]];

interface Vox { x: number; y: number; z: number; li: number; c: number; r: number; zi: number }
function buildWord(w: string): Vox[] {
  const vox: Vox[] = [], W = w.length * 7 - 2;
  for (let li = 0; li < w.length; li++) {
    const g = FONT[w[li]];
    for (let r = 0; r < 7; r++) for (let c = 0; c < 5; c++) if (g[r][c] === '#')
      for (let zi = 0; zi < 2; zi++) vox.push({ x: li * 7 + c - (W - 1) / 2, y: 6 - r + .5, z: zi - .5, li, c, r, zi });
  }
  return vox;
}
function vcol(v: Vox, flip: number): V3 {
  const b = LPAL[v.li % 6];
  return ((v.c + v.r + v.zi + flip) & 1) ? [lerp(b[0], 1, .7), lerp(b[1], 1, .7), lerp(b[2], 1, .7)] : b;
}

function cubeGeo(): Float32Array {
  const out: number[] = [];
  const cross = (a: number[], b: number[]) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  for (const n of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]) {
    const u = n[0] !== 0 ? [0, 1, 0] : [1, 0, 0], v = cross(n, u);
    for (const [a, b] of [[-1, -1], [1, -1], [1, 1], [-1, -1], [1, 1], [-1, 1]])
      out.push(n[0] * .5 + u[0] * a * .5 + v[0] * b * .5, n[1] * .5 + u[1] * a * .5 + v[1] * b * .5, n[2] * .5 + u[2] * a * .5 + v[2] * b * .5, n[0], n[1], n[2]);
  }
  return new Float32Array(out);
}

export default function bootvox(): Scene {
  let cube: Program, floor: Program, sky: Program;
  let vao: WebGLVertexArrayObject, instBuf: WebGLBuffer;
  const VOX = buildWord(WORD);
  const RN = (() => { const r = rng(7); return VOX.map(() => {
    const th = r() * 6.283, ph = Math.acos(2 * r() - 1), R0 = 30 + r() * 30;
    return { sx: Math.sin(ph) * Math.cos(th) * R0, sy: Math.abs(Math.cos(ph)) * R0 * .6 + 5, sz: Math.sin(ph) * Math.sin(th) * R0,
             d: r(), ax: r() - .5, ay: r() - .5, az: r() - .5 }; }); })();
  const INST = new Float32Array(VOX.length * 12);
  const SKY_VS = '#version 300 es\nconst vec2 P[3]=vec2[3](vec2(-1,-1),vec2(3,-1),vec2(-1,3));void main(){gl_Position=vec4(P[gl_VertexID],0,1);}';

  return {
    scale: 1, bloom: 1, crt: .5,
    init(e) {
      const gl = e.gl;
      cube = program(gl, VS_CUBE, FS_CUBE);
      floor = program(gl, VS_FLOOR, FS_FLOOR);
      sky = program(gl, SKY_VS, FS_SKY);
      vao = gl.createVertexArray()!;
      gl.bindVertexArray(vao);
      const vb = gl.createBuffer()!; gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.bufferData(gl.ARRAY_BUFFER, cubeGeo(), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
      gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);
      instBuf = gl.createBuffer()!; gl.bindBuffer(gl.ARRAY_BUFFER, instBuf); gl.bufferData(gl.ARRAY_BUFFER, INST.byteLength, gl.DYNAMIC_DRAW);
      for (let k = 0; k < 3; k++) { gl.enableVertexAttribArray(2 + k); gl.vertexAttribPointer(2 + k, 4, gl.FLOAT, false, 48, k * 16); gl.vertexAttribDivisor(2 + k, 1); }
      gl.bindVertexArray(null);
    },
    frame(f) {
      const gl = f.gl, e = f.e;
      const ov = /#boott=([\d.]+)/.exec(location.hash); // deterministic time for screenshots
      const t = ov ? +ov[1] : f.t;
      const asp = Math.max(1, 1.7 / (f.W / f.H));
      // assemble progress over ~4.5s, then gentle hold
      const P = clamp(t / 4.5, 0, 1.4);
      const flip = Math.floor((f.beat + 1) / 2) & 1;
      for (let i = 0; i < VOX.length; i++) {
        const v = VOX[i], q = RN[i];
        const pr = clamp((P - q.d * .5) / .5, 0, 1), ease = 1 - Math.pow(1 - pr, 3);
        const c = vcol(v, flip);
        const hop = P >= 1 ? 1.0 * Math.pow(Math.abs(Math.sin((f.beat - v.li * .33) * Math.PI * .5)), 3) : 0;
        const x = lerp(q.sx, v.x, ease);
        const y = lerp(q.sy, v.y + hop, ease);
        const z = lerp(q.sz, v.z, ease);
        const o = i * 12;
        INST[o] = x; INST[o + 1] = y; INST[o + 2] = z; INST[o + 3] = .92 * Math.min(1, pr * 3);
        INST[o + 4] = c[0]; INST[o + 5] = c[1]; INST[o + 6] = c[2]; INST[o + 7] = 1;
        INST[o + 8] = q.ax; INST[o + 9] = q.ay; INST[o + 10] = q.az; INST[o + 11] = (1 - ease) * 9;
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, instBuf); gl.bufferSubData(gl.ARRAY_BUFFER, 0, INST);

      const ang = Math.sin(f.t * .2) * .5, R = (44 - 10 * Math.min(1, P)) * asp;
      const eye: V3 = [Math.sin(ang) * R, 9 - 4 * Math.min(1, P), Math.cos(ang) * R];
      const tgt: V3 = [0, 3.8, 0];
      const Pm = perspective(.8, f.W / f.H, .1, 500), Vm = lookAt(eye, tgt), VP = mmul(Pm, Vm);
      // inverse VP for the sky (simple analytic inverse via re-derivation is costly; reuse mmul path)
      const IVP = invert(VP);

      // sky
      e.u(sky).v2('R', f.W, f.H).f('T', f.t).f('K', f.kick).m4('IVP', IVP);
      gl.bindVertexArray(e.vao); gl.drawArrays(gl.TRIANGLES, 0, 3);
      // cubes (up), mirrored (down), floor between
      gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.clear(gl.DEPTH_BUFFER_BIT);
      drawCubes(gl, e, VP, eye, -1, smooth(clamp(P, 0, 1)));
      gl.disable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      e.u(floor).v2('R', f.W, f.H).f('T', f.t).f('K', f.kick).m4('VP', VP).v3('CAM', eye).v3('GC', [.75, .3, 1]).v3('HC', [.25, .06, .33]).f('A', .5);
      gl.bindVertexArray(e.vao); gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disable(gl.BLEND); gl.enable(gl.DEPTH_TEST); gl.clear(gl.DEPTH_BUFFER_BIT);
      drawCubes(gl, e, VP, eye, 1, 1);
      gl.disable(gl.DEPTH_TEST);

      // overlay text
      const tx = f.text;
      tx.save();
      const introA = clamp(f.t / 1, 0, 1) * clamp((6 - f.t) / 1, 0, 1);
      tx.globalAlpha = 0.9;
      tx.font = "700 40px Bungee, 'Arial Black', sans-serif";
      tx.textAlign = 'center'; tx.textBaseline = 'middle';
      tx.fillStyle = '#efe4ff'; tx.shadowColor = '#ff4fd8'; tx.shadowBlur = 18;
      if (introA > 0.02) { tx.globalAlpha = introA; tx.fillText('GB4K PRESENTS ...', 640, 90); }
      tx.restore();
      return true;

      function drawCubes(gl2: WebGL2RenderingContext, en: import('./Engine').Engine, vp: Float32Array, camv: V3, m: number, mfv: number) {
        en.u(cube).v2('R', f.W, f.H).f('T', f.t).f('K', f.kick).m4('VP', vp).f('M', m).v3('CAM', camv).f('MF', mfv);
        gl2.bindVertexArray(vao);
        gl2.drawArraysInstanced(gl2.TRIANGLES, 0, 36, VOX.length);
      }
    },
    dispose(e) { const gl = e.gl; deleteProgram(gl, cube); deleteProgram(gl, floor); deleteProgram(gl, sky); gl.deleteVertexArray(vao); gl.deleteBuffer(instBuf); },
  };
}

// 4x4 matrix inverse (column-major), for the sky ray reconstruction.
function invert(m: Float32Array): Float32Array {
  const i = new Float32Array(16);
  i[0]=m[5]*m[10]*m[15]-m[5]*m[11]*m[14]-m[9]*m[6]*m[15]+m[9]*m[7]*m[14]+m[13]*m[6]*m[11]-m[13]*m[7]*m[10];
  i[4]=-m[4]*m[10]*m[15]+m[4]*m[11]*m[14]+m[8]*m[6]*m[15]-m[8]*m[7]*m[14]-m[12]*m[6]*m[11]+m[12]*m[7]*m[10];
  i[8]=m[4]*m[9]*m[15]-m[4]*m[11]*m[13]-m[8]*m[5]*m[15]+m[8]*m[7]*m[13]+m[12]*m[5]*m[11]-m[12]*m[7]*m[9];
  i[12]=-m[4]*m[9]*m[14]+m[4]*m[10]*m[13]+m[8]*m[5]*m[14]-m[8]*m[6]*m[13]-m[12]*m[5]*m[10]+m[12]*m[6]*m[9];
  i[1]=-m[1]*m[10]*m[15]+m[1]*m[11]*m[14]+m[9]*m[2]*m[15]-m[9]*m[3]*m[14]-m[13]*m[2]*m[11]+m[13]*m[3]*m[10];
  i[5]=m[0]*m[10]*m[15]-m[0]*m[11]*m[14]-m[8]*m[2]*m[15]+m[8]*m[3]*m[14]+m[12]*m[2]*m[11]-m[12]*m[3]*m[10];
  i[9]=-m[0]*m[9]*m[15]+m[0]*m[11]*m[13]+m[8]*m[1]*m[15]-m[8]*m[3]*m[13]-m[12]*m[1]*m[11]+m[12]*m[3]*m[9];
  i[13]=m[0]*m[9]*m[14]-m[0]*m[10]*m[13]-m[8]*m[1]*m[14]+m[8]*m[2]*m[13]+m[12]*m[1]*m[10]-m[12]*m[2]*m[9];
  i[2]=m[1]*m[6]*m[15]-m[1]*m[7]*m[14]-m[5]*m[2]*m[15]+m[5]*m[3]*m[14]+m[13]*m[2]*m[7]-m[13]*m[3]*m[6];
  i[6]=-m[0]*m[6]*m[15]+m[0]*m[7]*m[14]+m[4]*m[2]*m[15]-m[4]*m[3]*m[14]-m[12]*m[2]*m[7]+m[12]*m[3]*m[6];
  i[10]=m[0]*m[5]*m[15]-m[0]*m[7]*m[13]-m[4]*m[1]*m[15]+m[4]*m[3]*m[13]+m[12]*m[1]*m[7]-m[12]*m[3]*m[5];
  i[14]=-m[0]*m[5]*m[14]+m[0]*m[6]*m[13]+m[4]*m[1]*m[14]-m[4]*m[2]*m[13]-m[12]*m[1]*m[6]+m[12]*m[2]*m[5];
  i[3]=-m[1]*m[6]*m[11]+m[1]*m[7]*m[10]+m[5]*m[2]*m[11]-m[5]*m[3]*m[10]-m[9]*m[2]*m[7]+m[9]*m[3]*m[6];
  i[7]=m[0]*m[6]*m[11]-m[0]*m[7]*m[10]-m[4]*m[2]*m[11]+m[4]*m[3]*m[10]+m[8]*m[2]*m[7]-m[8]*m[3]*m[6];
  i[11]=-m[0]*m[5]*m[11]+m[0]*m[7]*m[9]+m[4]*m[1]*m[11]-m[4]*m[3]*m[9]-m[8]*m[1]*m[7]+m[8]*m[3]*m[5];
  i[15]=m[0]*m[5]*m[10]-m[0]*m[6]*m[9]-m[4]*m[1]*m[10]+m[4]*m[2]*m[9]+m[8]*m[1]*m[6]-m[8]*m[2]*m[5];
  let det = m[0]*i[0]+m[1]*i[4]+m[2]*i[8]+m[3]*i[12]; det = det ? 1 / det : 0;
  for (let k = 0; k < 16; k++) i[k] *= det;
  return i;
}
